import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ApiLogEntry } from '../../types';
import { useLocalization } from '../../hooks/useLocalization';
import { usePageContent } from '../../hooks/usePageContent';

interface FlightsPageProps {
    logApiCall: (log: ApiLogEntry) => void;
    isSettingsReady: boolean;
}

interface Location {
    id: string | number;
    name: string;
    type?: number;
    country?: string;
}

interface FlightResult {
    hotelId?: string;
    hotelName?: string;
    price: number;
    currency: string;
    night: number;
    stars?: number;
    boardType?: string;
    outboundFlight?: {
        departureTime: string;
        arrivalTime: string;
        departureAirport: string;
        arrivalAirport: string;
        airline: string;
        flightNumber: string;
        duration: string;
        stops: number;
    };
    returnFlight?: {
        departureTime: string;
        arrivalTime: string;
        departureAirport: string;
        arrivalAirport: string;
        airline: string;
        flightNumber: string;
        duration: string;
        stops: number;
    };
}

// ─── Debounce Hook ──────────────────────────────────────────────
function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

// ─── Autocomplete Input Component ───────────────────────────────
const LocationAutocomplete: React.FC<{
    label: string;
    placeholder: string;
    endpoint: string;
    value: Location | null;
    onChange: (loc: Location | null) => void;
    icon: string;
}> = ({ label, placeholder, endpoint, value, onChange, icon }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Location[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const debouncedQuery = useDebounce(query, 300);

    useEffect(() => {
        if (debouncedQuery.length < 2) { setSuggestions([]); return; }
        const fetchSuggestions = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${endpoint}?q=${encodeURIComponent(debouncedQuery)}`);
                const data = await res.json();
                setSuggestions(data.locations || []);
                setShowDropdown(true);
            } catch { setSuggestions([]); }
            finally { setLoading(false); }
        };
        fetchSuggestions();
    }, [debouncedQuery, endpoint]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={wrapperRef} className="relative flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">{icon}</span>
                <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 transition-all text-sm shadow-sm"
                    placeholder={placeholder}
                    value={value ? value.name : query}
                    onChange={(e) => { setQuery(e.target.value); if (value) onChange(null); }}
                    onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                />
                {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-sky-500 border-t-transparent rounded-full"></div>}
            </div>
            {showDropdown && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto">
                    {suggestions.map((loc, i) => (
                        <button
                            key={`${loc.id}-${i}`}
                            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-sky-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0"
                            onClick={() => { onChange(loc); setQuery(''); setShowDropdown(false); }}
                        >
                            <span className="text-sky-500">📍</span>
                            <div>
                                <p className="font-medium text-gray-900">{loc.name}</p>
                                {loc.country && <p className="text-xs text-gray-400">{loc.country}</p>}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Flight Result Card ─────────────────────────────────────────
const FlightResultCard: React.FC<{ result: FlightResult }> = ({ result }) => {
    const formatTime = (dateStr?: string) => {
        if (!dateStr) return '--:--';
        try { return new Date(dateStr).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }); }
        catch { return '--:--'; }
    };
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        try { return new Date(dateStr).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }); }
        catch { return ''; }
    };

    const renderSegment = (segment: FlightResult['outboundFlight'], icon: string, color: string) => {
        if (!segment) return null;
        return (
            <div className="flex items-center gap-4 py-3">
                <span className={`text-2xl ${color}`}>{icon}</span>
                <div className="flex-1 flex items-center gap-3 min-w-0">
                    <div className="text-center shrink-0">
                        <p className="text-gray-900 font-bold text-lg leading-tight">{formatTime(segment.departureTime)}</p>
                        <p className="text-[11px] text-gray-400">{formatDate(segment.departureTime)}</p>
                        <p className="text-xs font-mono text-gray-500 mt-0.5">{segment.departureAirport}</p>
                    </div>
                    <div className="flex-1 flex flex-col items-center px-2 min-w-[80px]">
                        <span className="text-[10px] text-gray-400">{segment.duration || ''}</span>
                        <div className="w-full h-px bg-gray-300 my-1 relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-sky-400 rounded-full"></div>
                        </div>
                        <span className="text-[10px] text-sky-500 font-medium">
                            {segment.stops === 0 ? 'Direct' : `${segment.stops} stop(s)`}
                        </span>
                    </div>
                    <div className="text-center shrink-0">
                        <p className="text-gray-900 font-bold text-lg leading-tight">{formatTime(segment.arrivalTime)}</p>
                        <p className="text-[11px] text-gray-400">{formatDate(segment.arrivalTime)}</p>
                        <p className="text-xs font-mono text-gray-500 mt-0.5">{segment.arrivalAirport}</p>
                    </div>
                </div>
                <div className="text-right shrink-0 min-w-[90px]">
                    <p className="text-sm text-gray-600 font-medium">{segment.airline}</p>
                    <p className="text-xs text-gray-400 font-mono">{segment.flightNumber}</p>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group">
            <div className="p-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center text-sky-600 text-xl">✈️</div>
                        <div>
                            <p className="text-gray-900 font-bold text-sm">{result.hotelName || 'Flight Package'}</p>
                            {result.stars && (
                                <div className="flex gap-0.5 mt-0.5">
                                    {Array.from({ length: result.stars }).map((_, i) => <span key={i} className="text-amber-400 text-xs">★</span>)}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-black text-gray-900">{result.price.toLocaleString()}<span className="text-sm font-normal text-gray-400 ml-1">{result.currency}</span></p>
                        <p className="text-xs text-green-600 font-medium">{result.night} nights • {result.boardType || 'All Inclusive'}</p>
                    </div>
                </div>

                {/* Segments */}
                <div className="divide-y divide-gray-100">
                    {renderSegment(result.outboundFlight, '🛫', 'text-green-600')}
                    {renderSegment(result.returnFlight, '🛬', 'text-orange-500')}
                </div>

                {!result.outboundFlight && !result.returnFlight && (
                    <div className="py-4 text-center bg-gray-50 rounded-xl mt-2">
                        <p className="text-sm text-gray-400">Flight details available after offer selection</p>
                    </div>
                )}
            </div>

            {/* CTA */}
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">Price for selected period incl. flights + accommodation</p>
                <button className="px-5 py-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm rounded-xl shadow-sm transition-all hover:shadow-md active:scale-95">
                    View Details
                </button>
            </div>
        </div>
    );
};

// ─── Main FlightsPage Component ─────────────────────────────────
export const FlightsPage: React.FC<FlightsPageProps> = ({ logApiCall, isSettingsReady }) => {
    const { t, language } = useLocalization();
    const { content } = usePageContent('flights', language);

    const [departure, setDeparture] = useState<Location | null>(null);
    const [arrival, setArrival] = useState<Location | null>(null);
    const [checkIn, setCheckIn] = useState('');
    const [night, setNight] = useState(7);
    const [adults, setAdults] = useState(2);
    const [children, setChildren] = useState(0);

    const [results, setResults] = useState<FlightResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // CMS content with fallbacks
    const heroTitle = content?.hero?.title || t('flights_hero_title') || 'Find Your Wings';
    const heroSubtitle = content?.hero?.subtitle || t('flights_hero_subtitle') || 'Search and compare flights.';
    const heroImage = content?.hero?.imageUrl || 'https://images.unsplash.com/photo-1542296332-2e44a99cfef9?q=80&w=2000&auto=format&fit=crop';

    useEffect(() => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        setCheckIn(d.toISOString().split('T')[0]);
    }, []);

    const handleSearch = useCallback(async () => {
        if (!departure || !arrival || !checkIn) {
            setError('Please select departure, destination, and date.');
            return;
        }

        setLoading(true);
        setError(null);
        setResults([]);
        setHasSearched(true);

        try {
            const res = await fetch('/api/flights/search', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    departureId: departure.id,
                    arrivalId: arrival.id,
                    checkIn,
                    night,
                    adults,
                    children
                })
            });

            const data = await res.json();
            if (!data.success) throw new Error(data.message);

            setTotalCount(data.totalCount || 0);

            const mapped: FlightResult[] = (data.hotels || []).map((hotel: any) => {
                const offer = hotel.offers?.[0];
                const outbound = hotel.outboundFlight || offer?.outboundFlight;
                const returnFl = hotel.returnFlight || offer?.returnFlight;
                return {
                    hotelId: hotel.id,
                    hotelName: hotel.name || hotel.hotelName,
                    price: offer?.price?.amount || hotel.price || 0,
                    currency: offer?.price?.currency || hotel.currency || 'EUR',
                    night: hotel.night || night,
                    stars: hotel.stars,
                    boardType: offer?.boardName || hotel.boardType,
                    outboundFlight: outbound ? {
                        departureTime: outbound.departureTime || outbound.departureDate,
                        arrivalTime: outbound.arrivalTime || outbound.arrivalDate,
                        departureAirport: outbound.departureAirport || outbound.departureCode || '',
                        arrivalAirport: outbound.arrivalAirport || outbound.arrivalCode || '',
                        airline: outbound.airline || outbound.providerName || '',
                        flightNumber: outbound.flightNumber || outbound.flightNo || '',
                        duration: outbound.duration || '',
                        stops: outbound.stops || 0,
                    } : undefined,
                    returnFlight: returnFl ? {
                        departureTime: returnFl.departureTime || returnFl.departureDate,
                        arrivalTime: returnFl.arrivalTime || returnFl.arrivalDate,
                        departureAirport: returnFl.departureAirport || returnFl.departureCode || '',
                        arrivalAirport: returnFl.arrivalAirport || returnFl.arrivalCode || '',
                        airline: returnFl.airline || returnFl.providerName || '',
                        flightNumber: returnFl.flightNumber || returnFl.flightNo || '',
                        duration: returnFl.duration || '',
                        stops: returnFl.stops || 0,
                    } : undefined,
                };
            });

            setResults(mapped);

            // Log API call
            logApiCall({
                timestamp: new Date().toISOString(),
                request: { departureId: departure.id, arrivalId: arrival.id, checkIn, night },
                response: JSON.stringify({ totalCount: data.totalCount }),
                endpoint: '/api/flights/search',
                status: 'success'
            });
        } catch (err: any) {
            setError(err.message || 'Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [departure, arrival, checkIn, night, adults, children, logApiCall]);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero */}
            <div className="relative h-[55vh] min-h-[480px] w-full overflow-hidden">
                <div className="absolute inset-0">
                    <img src={heroImage} className="w-full h-full object-cover transform scale-105" alt="Flight Search" />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-black/40"></div>
                </div>
                <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center pb-32">
                    <span className="inline-block py-1 px-4 rounded-full bg-sky-500 text-white text-xs font-bold uppercase tracking-widest mb-6 shadow-lg">
                        {content?.hero?.badgeText || t('landing_flights_badge') || '✈ Fly High'}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 drop-shadow-xl">{heroTitle}</h1>
                    <p className="text-xl text-white/90 max-w-2xl drop-shadow-md">{heroSubtitle}</p>
                </div>
            </div>

            {/* Search Widget */}
            <div className="container mx-auto px-4 relative z-20 -mt-28 mb-12">
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 md:p-8">
                    <div className="flex flex-col lg:flex-row gap-4 mb-4">
                        <LocationAutocomplete
                            label={t('departure') || 'Departure'}
                            placeholder={t('departure_airport') || 'Search departure...'}
                            endpoint="/api/flights/departures"
                            value={departure}
                            onChange={setDeparture}
                            icon="🛫"
                        />
                        <LocationAutocomplete
                            label={t('destination') || 'Destination'}
                            placeholder={t('arrival_airport') || 'Search destination...'}
                            endpoint="/api/flights/arrivals"
                            value={arrival}
                            onChange={setArrival}
                            icon="🛬"
                        />
                        <div className="flex-1 min-w-[160px]">
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('check_in_date') || 'Check-in'}</label>
                            <input
                                type="date"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 transition-all text-sm shadow-sm"
                                value={checkIn}
                                onChange={(e) => setCheckIn(e.target.value)}
                            />
                        </div>
                        <div className="min-w-[120px]">
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('night') || 'Nights'}</label>
                            <select
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 transition-all text-sm shadow-sm appearance-none"
                                value={night}
                                onChange={(e) => setNight(parseInt(e.target.value))}
                            >
                                {[3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((n) => (
                                    <option key={n} value={n}>{n} {t('nights') || 'nights'}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-end justify-between gap-4">
                        <div className="flex gap-6">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('adults') || 'Adults'}</label>
                                <div className="flex items-center gap-2">
                                    <button className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center justify-center transition-colors font-bold"
                                        onClick={() => setAdults(Math.max(1, adults - 1))}>−</button>
                                    <span className="w-6 text-center text-gray-900 font-bold">{adults}</span>
                                    <button className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center justify-center transition-colors font-bold"
                                        onClick={() => setAdults(Math.min(6, adults + 1))}>+</button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('children') || 'Children'}</label>
                                <div className="flex items-center gap-2">
                                    <button className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center justify-center transition-colors font-bold"
                                        onClick={() => setChildren(Math.max(0, children - 1))}>−</button>
                                    <span className="w-6 text-center text-gray-900 font-bold">{children}</span>
                                    <button className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center justify-center transition-colors font-bold"
                                        onClick={() => setChildren(Math.min(4, children + 1))}>+</button>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={loading || !departure || !arrival || !checkIn}
                            className="px-10 py-3.5 bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 disabled:from-gray-300 disabled:to-gray-300 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2.5 active:scale-95 text-sm"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                    {t('searching_flights_status') || 'Searching...'}
                                </>
                            ) : (
                                <>🔎 {t('search') || 'Search Flights'}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="container mx-auto px-4 mb-6">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                        <span className="text-red-500">⚠️</span>
                        <p className="text-red-600 text-sm flex-1">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-xl">×</button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="container mx-auto px-4">
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="relative">
                            <div className="animate-spin rounded-full h-16 w-16 border-4 border-sky-200 border-t-sky-500"></div>
                            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl">✈️</span>
                        </div>
                        <p className="text-gray-500 mt-6 animate-pulse font-medium">{t('searching_flights_status') || 'Searching for flights...'}</p>
                        <p className="text-xs text-gray-400 mt-2">This may take 15-30 seconds</p>
                    </div>
                </div>
            )}

            {/* Results */}
            {!loading && hasSearched && results.length > 0 && (
                <div className="container mx-auto px-4 pb-20">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-900">
                            ✈️ {totalCount} {totalCount === 1 ? 'result' : 'results'} found
                        </h2>
                        <select className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 shadow-sm">
                            <option>Price (Low to High)</option>
                            <option>Price (High to Low)</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {results.map((result, i) => (
                            <FlightResultCard key={i} result={result} />
                        ))}
                    </div>
                </div>
            )}

            {/* No Results */}
            {!loading && hasSearched && results.length === 0 && !error && (
                <div className="container mx-auto px-4 pb-20 text-center py-20">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{t('no_flights_found_msg') || 'No flights found'}</h3>
                    <p className="text-sm text-gray-500">Try different dates or destinations</p>
                </div>
            )}

            {/* Features — shown before search */}
            {!hasSearched && !loading && (
                <div className="container mx-auto px-4 pb-20">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-lg transition-shadow">
                            <div className="w-12 h-12 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center mx-auto mb-4 text-xl">✈️</div>
                            <h3 className="font-bold text-gray-900 text-lg mb-2">Direct Flights</h3>
                            <p className="text-gray-500">Non-stop connections to major holiday hubs in the Mediterranean.</p>
                        </div>
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-lg transition-shadow">
                            <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-xl">🏷️</div>
                            <h3 className="font-bold text-gray-900 text-lg mb-2">Best Price Guarantee</h3>
                            <p className="text-gray-500">We bundle flights with accommodation to give you the lowest total price.</p>
                        </div>
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-lg transition-shadow">
                            <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-4 text-xl">⏰</div>
                            <h3 className="font-bold text-gray-900 text-lg mb-2">Reliable Schedules</h3>
                            <p className="text-gray-500">Flights operated by trusted airline partners with convenient times.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}